import { useQuery } from '@tanstack/react-query'
import { apiGet, type PlatformSummary } from '../lib/api.ts'

/*
 * Snapshot identity is first-class (CLAUDE.md rule 2): every canonical fact
 * traces to the snapshot that produced it, and snapshots are retained forever.
 * Making the list visible from day one keeps that provenance in front of the
 * operator rather than buried in a table.
 */

export function Snapshots() {
  const { data, isPending, error } = useQuery({
    queryKey: ['summary'],
    queryFn: () => apiGet<PlatformSummary>('/summary'),
  })

  const snapshots = data?.snapshots ?? []

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">Snapshots</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Every SOS file ever landed. Retained forever — the accumulated registration
        history cannot be recreated.
      </p>

      {error ? (
        <p className="mt-6 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
          Could not load snapshots: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : null}

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">Extract date</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Coverage</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Status</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Archive path</th>
              <th scope="col" className="py-2 font-semibold">Loaded</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={5} className="py-4 text-ink-muted">Loading…</td>
              </tr>
            ) : snapshots.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-4 text-ink-muted">No snapshots loaded.</td>
              </tr>
            ) : (
              snapshots.map((s) => (
                <tr key={s.archive_path} className="border-b border-line/60">
                  <td className="py-2 pr-4 font-mono tabular-nums">{s.extract_date}</td>
                  <td className="py-2 pr-4">{s.coverage}</td>
                  <td className="py-2 pr-4">
                    {/* Status is a word, not a colour — yellow stays reserved for warnings. */}
                    {s.status}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-ink-muted">{s.archive_path}</td>
                  <td className="py-2 text-ink-muted">
                    {s.loaded_at ? s.loaded_at.slice(0, 10) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
